import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import { getJournalById } from "api/debitjnl.service";
import { logError } from "api/diagnostic.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";
import { formatDate, formatNZD } from "common/utils";
import React, { useEffect, useState } from "react";
import type { JournalDataEntryForm } from "types/model.types";

interface IAdminDebitJournalPreviewProps {
  id: number;
}
const AdminDebitJournalPreview: React.FC<IAdminDebitJournalPreviewProps> = ({
  id,
}) => {
  const [exporting, setExporting] = useState(false);
  const [initialData, setInitialData] = useState<JournalDataEntryForm | null>(
    null,
  );

  useEffect(() => {
    if (!id) return;

    let mounted = true;
    const fetchData = async () => {
      try {
        const response = await safeApiCall(getJournalById(id));
        if (response && mounted) setInitialData(response);
      } catch (error) {
        logError(error);
        notifications.show({
          message: "Could not fetch journal details.",
          color: MantineNotificationColor.Error,
          position: MantineNotificationPosition.TopCenter,
        });
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [id]);

  return (
    <>
      <div className="sub-title">
        *** Debit Journal #{initialData?.summary.id} ***
      </div>
      <table className="print-table">
        <tbody>
          <tr>
            <td className="leftCol">
              <span className="rcl">Date Ordered:</span>
              <span className="rcv">
                {formatDate(
                  initialData?.summary.createdOn ?? new Date().toDateString(),
                )}
              </span>
            </td>
            <td className="rightCol">
              <div>
                <span className="rcl">Entered:</span>
                <span className="rcv">
                  {formatNZD(initialData?.summary.entered ?? 0)}
                </span>
              </div>
              <div>
                <span className="rcl">Expected:</span>
                <span className="rcv">
                  {formatNZD(initialData?.summary.expected ?? 0)}
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div className="spacer padTop padBottom" />
      <table className="print-table details-table">
        <thead>
          <tr>
            <th className="nowrap">Invoice #</th>
            <th className="nowrap">Debtor</th>
            <th className="description">Name</th>
            <th className="money">Amount</th>
          </tr>
        </thead>
        <tbody>
          {initialData?.entries.map((item) => (
            <tr key={item.id}>
              <td className="nowrap">{item.invoiceId}</td>
              <td className="nowrap">{item.debtorId}</td>
              <td className="description">{item.debtorName ?? 0}</td>
              <td className="money">{formatNZD(item.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default AdminDebitJournalPreview;
